import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { WorkspaceDialogState } from '../../types';

type WorkspaceDialogProps = {
  dialogState: WorkspaceDialogState;
  isSaving: boolean;
  title: string;
  externallyVisible: boolean;
  authType: 'None' | 'Password';
  password: string;
  deleteWithSpaces: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onTitleChange: (value: string) => void;
  onVisibleChange: (value: boolean) => void;
  onAuthTypeChange: (value: 'None' | 'Password') => void;
  onPasswordChange: (value: string) => void;
  onDeleteWithSpacesChange: (value: boolean) => void;
};

export const WorkspaceDialog = ({
  dialogState,
  isSaving,
  title,
  externallyVisible,
  authType,
  password,
  deleteWithSpaces,
  onClose,
  onSubmit,
  onTitleChange,
  onVisibleChange,
  onAuthTypeChange,
  onPasswordChange,
  onDeleteWithSpacesChange,
}: WorkspaceDialogProps) => {
  return (
    <Dialog open={dialogState.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {dialogState.mode === 'create'
          ? 'New workspace'
          : dialogState.mode === 'edit'
            ? 'Edit workspace'
            : 'Delete workspace'}
      </DialogTitle>
      <DialogContent>
        {dialogState.mode !== 'delete' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={title} onChange={(event) => onTitleChange(event.target.value)} fullWidth />
            <FormControlLabel
              control={
                <Switch
                  checked={externallyVisible}
                  onChange={(event) => onVisibleChange(event.target.checked)}
                />
              }
              label="Externally visible"
            />
            {externallyVisible && (
              <FormControl size="small" fullWidth>
                <InputLabel id="workspace-auth-label">Authentication</InputLabel>
                <Select
                  labelId="workspace-auth-label"
                  value={authType}
                  label="Authentication"
                  onChange={(event) => onAuthTypeChange(event.target.value as 'None' | 'Password')}
                >
                  <MenuItem value="None">None</MenuItem>
                  <MenuItem value="Password">Password</MenuItem>
                </Select>
              </FormControl>
            )}
            {externallyVisible && authType === 'Password' && (
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                fullWidth
              />
            )}
          </Stack>
        )}

        {dialogState.mode === 'delete' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>Are you sure you want to delete this workspace?</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={deleteWithSpaces}
                  onChange={(event) => onDeleteWithSpacesChange(event.target.checked)}
                />
              }
              label="Delete workspace and all associated spaces"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} disabled={isSaving} variant="contained" color="primary">
          {dialogState.mode === 'edit' ? 'Update' : dialogState.mode === 'delete' ? 'Delete' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
